import { useMemo, useState } from 'react';
import { Button, Collapse, Flex, Space, Switch, Typography } from 'antd';
import { IconDeviceFloppy, IconRotate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import type { GroupedPermissions } from '../../types';
import { ConfirmPermissionsModal } from '../ConfirmPermissionsModal/ConfirmPermissionsModal';
import { usePermissionAccordionItems } from './hooks/usePermissionAccordionItems';

const { Text } = Typography;

type RolePermissionsAccordionProps = {
  groupedPermissions: GroupedPermissions[];
  draftPermissionKeys: PermissionKey[];
  isDirty: boolean;
  isSaving: boolean;
  isPermissionsUpdating: boolean;
  isRoleEditable: boolean;
  isPermissionDirty: (permissionKey: PermissionKey) => boolean;
  isConfirmModalOpen: boolean;
  isEditableSystemRole: boolean;
  onTogglePermission: (permissionKey: PermissionKey) => void;
  onSetAllPermissions: (selectAll: boolean) => void;
  allPermissionsSelected: boolean;
  onConfirmChanges: () => void;
  onDiscardChanges: () => void;
  onConfirmModalClose: () => void;
  onConfirmSave: () => void;
};

export function RolePermissionsAccordion({
  groupedPermissions,
  draftPermissionKeys,
  isDirty,
  isSaving,
  isPermissionsUpdating,
  isRoleEditable,
  isPermissionDirty,
  isConfirmModalOpen,
  isEditableSystemRole,
  onTogglePermission,
  onSetAllPermissions,
  allPermissionsSelected,
  onConfirmChanges,
  onDiscardChanges,
  onConfirmModalClose,
  onConfirmSave,
}: RolePermissionsAccordionProps) {
  const { t } = useTranslation('authorization');
  const categoryKeys = useMemo(
    () => groupedPermissions.map((group) => group.category),
    [groupedPermissions],
  );
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const allExpanded =
    categoryKeys.length > 0 &&
    categoryKeys.every((key) => activeKeys.includes(key));

  const { items } = usePermissionAccordionItems({
    groupedPermissions,
    draftPermissionKeys,
    isRoleEditable,
    isPermissionDirty,
    suppressDirtyHighlight: isPermissionsUpdating,
    onTogglePermission,
  });

  const handleExpandAllToggle = (expandAll: boolean) => {
    setActiveKeys(expandAll ? [...categoryKeys] : []);
  };

  return (
    <Flex
      vertical
      gap={16}
      className={[
        'authorization-permissions-accordion',
        isPermissionsUpdating
          ? 'authorization-permissions-accordion--updating'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Flex vertical gap={12} className="authorization-permissions-controls">
        <Flex justify="space-between" align="center" gap={12} wrap>
          <Flex
            align="center"
            gap={10}
            className="authorization-permissions-expand-toggle"
          >
            <Switch
              size="small"
              checked={allExpanded}
              onChange={handleExpandAllToggle}
              aria-label={t('detail.expandAllModules')}
            />
            <Text className="authorization-permissions-expand-label">
              {t('detail.expandAllModules')}
            </Text>
            <Text
              type="secondary"
              className="authorization-permissions-expand-state"
            >
              {allExpanded ? t('detail.collapseAll') : t('detail.expandAll')}
            </Text>
          </Flex>

          <Text
            type="secondary"
            className="authorization-permissions-module-count"
          >
            {t('detail.moduleCount', { count: categoryKeys.length })}
          </Text>
        </Flex>

        {isRoleEditable && (
          <Flex
            align="center"
            gap={10}
            className="authorization-permissions-expand-toggle"
          >
            <Switch
              size="small"
              checked={allPermissionsSelected}
              disabled={isSaving || isPermissionsUpdating}
              onChange={onSetAllPermissions}
              aria-label={t('detail.selectAllPermissions')}
            />
            <Text className="authorization-permissions-expand-label">
              {t('detail.selectAllPermissions')}
            </Text>
            <Text
              type="secondary"
              className="authorization-permissions-expand-state"
            >
              {allPermissionsSelected
                ? t('detail.deselectAll')
                : t('detail.selectAll')}
            </Text>
          </Flex>
        )}
      </Flex>

      {isRoleEditable && isDirty && (
        <Flex
          className="authorization-permissions-toolbar authorization-permissions-toolbar--visible"
          justify="space-between"
          align="center"
          gap={12}
          wrap
        >
          <Text className="authorization-permissions-toolbar-hint">
            {t('detail.unsavedChanges')}
          </Text>
          <Space>
            <Button
              icon={<IconRotate size={16} />}
              onClick={onDiscardChanges}
              disabled={isSaving}
            >
              {t('detail.discard')}
            </Button>
            <Button
              type="primary"
              icon={<IconDeviceFloppy size={16} />}
              loading={isSaving}
              onClick={onConfirmChanges}
            >
              {t('detail.confirmChanges')}
            </Button>
          </Space>
        </Flex>
      )}

      {!isRoleEditable && (
        <Text className="authorization-permissions-readonly-hint">
          {t('detail.readOnlyPermissionsHint')}
        </Text>
      )}

      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => {
          if (Array.isArray(keys)) {
            setActiveKeys(keys);
            return;
          }
          setActiveKeys(keys ? [keys] : []);
        }}
        items={items}
        className="authorization-permissions-collapse"
        bordered={false}
      />

      <ConfirmPermissionsModal
        open={isConfirmModalOpen}
        isSystemRole={isEditableSystemRole}
        loading={isSaving}
        onCancel={onConfirmModalClose}
        onConfirm={onConfirmSave}
      />
    </Flex>
  );
}
